/**
 * SearchPage - Gelişmiş Arama Sayfası
 * NIP-50 ile gelişmiş filtreleme özellikleri
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Filter, Calendar, User, Hash, Loader2, X, ChevronDown, FileText, MessageSquare } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useSearchNostr, SearchFilters } from '../hooks/useSearchNostr'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { UserAvatar } from '../components/Common/UserAvatar'

// Time range presets
const TIME_PRESETS = [
  { label: 'Bugün', value: 'today' },
  { label: 'Bu Hafta', value: 'week' },
  { label: 'Bu Ay', value: 'month' },
  { label: 'Son 3 Ay', value: '3months' },
  { label: 'Tüm Zamanlar', value: 'all' },
]

// Kind presets
const KIND_PRESETS = [
  { label: 'Tümü', kinds: [1, 30023], icon: Search },
  { label: 'Entry\'ler', kinds: [1], icon: MessageSquare },
  { label: 'Makaleler', kinds: [30023], icon: FileText },
]

function SearchResultCard({ event }: { event: any }) {
  const { metadata } = useNostrProfile(event.pubkey)
  const displayName = metadata?.display_name || metadata?.name || event.pubkey.slice(0, 8) + '...'
  const avatar = metadata?.picture || null
  const isArticle = event.kind === 30023

  // Extract title for articles
  const title = isArticle
    ? event.tags.find((t: string[]) => t[0] === 'title')?.[1]
    : null

  // Get hashtags
  const hashtags = event.tags
    .filter((t: string[]) => t[0] === 't')
    .map((t: string[]) => t[1])
    .slice(0, 5)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-700 hover:border-mavi-300 dark:hover:border-mavi-700 transition-colors"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <Link to={`/profile/${event.pubkey}`}>
          <UserAvatar
            src={avatar}
            name={displayName}
            size="md"
            isExternal={!event.tags.some((t: string[]) => t[0] === 't' && ['mavililer', 'mavililersözlük', 'mavililersozluk'].includes(t[1].toLowerCase()))}
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${event.pubkey}`} className="font-poppins font-medium text-sm hover:text-mavi-500 transition-colors">
            {displayName}
          </Link>
          <p className="text-xs text-gray-500">
            {formatDistanceToNow(new Date(event.created_at * 1000), { addSuffix: true, locale: tr })}
          </p>
        </div>
        {isArticle && (
          <span className="px-2 py-1 text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full">
            Makale
          </span>
        )}
      </div>

      {/* Title for articles */}
      {title && (
        <h3 className="font-poppins font-semibold text-base mb-2 text-gray-900 dark:text-white">
          {title}
        </h3>
      )}

      {/* Content */}
      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 mb-3">
        {event.content.slice(0, 300)}{event.content.length > 300 ? '...' : ''}
      </p>

      {/* Hashtags */}
      {hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {hashtags.map((tag: string) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-[10px] bg-surface-100 dark:bg-surface-800 text-gray-600 dark:text-gray-400 rounded-full"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Link */}
      <div className="mt-3 pt-3 border-t border-surface-100 dark:border-surface-800">
        <Link
          to={isArticle ? `/article/${event.id}` : `/entry/${event.id}`}
          className="text-xs text-mavi-500 hover:text-mavi-600 font-medium"
        >
          Görüntüle →
        </Link>
      </div>
    </motion.div>
  )
}

export function SearchPage() {
  const [query, setQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedKinds, setSelectedKinds] = useState<number[]>([1, 30023])
  const [timePreset, setTimePreset] = useState('all')
  const [turkishOnly, setTurkishOnly] = useState(true)
  const [authorFilter, setAuthorFilter] = useState('')
  const [hashtagFilter, setHashtagFilter] = useState('')

  const { results, isSearching, advancedSearch, clearResults } = useSearchNostr()

  const getTimeRange = (): { since?: number; until?: number } => {
    const now = Math.floor(Date.now() / 1000)
    switch (timePreset) {
      case 'today':
        return { since: now - 86400 }
      case 'week':
        return { since: now - 604800 }
      case 'month':
        return { since: now - 2592000 }
      case '3months':
        return { since: now - 7776000 }
      default:
        return {}
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    const filters: SearchFilters = {
      query,
      kinds: selectedKinds,
      turkishOnly,
      limit: 50,
      ...getTimeRange(),
    }

    // Add author filter if provided
    if (authorFilter.trim()) {
      filters.authors = [authorFilter.trim()]
    }

    // Add hashtag filter if provided
    if (hashtagFilter.trim()) {
      filters.hashtags = hashtagFilter.split(',').map(h => h.trim().replace('#', ''))
    }

    advancedSearch(filters)
  }

  const clearAll = () => {
    setQuery('')
    setSelectedKinds([1, 30023])
    setTimePreset('all')
    setTurkishOnly(true)
    setAuthorFilter('')
    setHashtagFilter('')
    clearResults()
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mb-2">
          Gelişmiş Arama
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nostr'da içerik arayın, filtreleyin ve keşfedin
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Arama terimi girin..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 focus:outline-none focus:ring-2 focus:ring-mavi-500/20 focus:border-mavi-500 transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-xl border transition-all flex items-center gap-2 ${showFilters
                ? 'bg-mavi-500 text-white border-mavi-500'
                : 'border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
          >
            <Filter className="w-5 h-5" />
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button
            type="submit"
            disabled={isSearching || !query.trim()}
            className="px-6 py-3 bg-mavi-500 hover:bg-mavi-600 disabled:bg-gray-400 text-white rounded-xl font-poppins font-medium transition-colors flex items-center gap-2"
          >
            {isSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            Ara
          </button>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 p-4 bg-surface-50 dark:bg-surface-800/50 rounded-xl border border-surface-200 dark:border-surface-700 space-y-4">
                {/* Kind Filter */}
                <div>
                  <label className="text-xs font-poppins font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                    İçerik Türü
                  </label>
                  <div className="flex gap-2">
                    {KIND_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => setSelectedKinds(preset.kinds)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${JSON.stringify(selectedKinds) === JSON.stringify(preset.kinds)
                            ? 'bg-mavi-500 text-white'
                            : 'bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 hover:border-mavi-300'
                          }`}
                      >
                        <preset.icon className="w-4 h-4" />
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Filter */}
                <div>
                  <label className="text-xs font-poppins font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Zaman Aralığı
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TIME_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => setTimePreset(preset.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${timePreset === preset.value
                            ? 'bg-mavi-500 text-white'
                            : 'bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 hover:border-mavi-300'
                          }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Author & Hashtag Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-poppins font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Yazar (pubkey)
                    </label>
                    <input
                      type="text"
                      value={authorFilter}
                      onChange={(e) => setAuthorFilter(e.target.value)}
                      placeholder="npub veya hex pubkey"
                      className="w-full px-3 py-2 rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/20"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-poppins font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      Hashtag'ler
                    </label>
                    <input
                      type="text"
                      value={hashtagFilter}
                      onChange={(e) => setHashtagFilter(e.target.value)}
                      placeholder="bitcoin, nostr, türkiye"
                      className="w-full px-3 py-2 rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/20"
                    />
                  </div>
                </div>

                {/* Turkish Only Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Sadece Türkçe içerik
                  </span>
                  <button
                    type="button"
                    onClick={() => setTurkishOnly(!turkishOnly)}
                    className={`w-12 h-6 rounded-full transition-colors relative ${turkishOnly ? 'bg-mavi-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${turkishOnly ? 'left-7' : 'left-1'
                        }`}
                    />
                  </button>
                </div>

                {/* Clear Button */}
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Filtreleri Temizle
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* Results */}
      {isSearching ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-mavi-500 mb-4" />
          <p className="text-sm text-gray-500">Aranıyor...</p>
        </div>
      ) : results.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {results.length} sonuç bulundu
          </p>
          {results.map((event) => (
            <SearchResultCard key={event.id} event={event} />
          ))}
        </div>
      ) : query ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Sonuç bulunamadı</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Farklı anahtar kelimeler veya filtreler deneyin
          </p>
        </div>
      ) : (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Aramaya başlayın</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Nostr'daki içerikleri keşfedin
          </p>
        </div>
      )}
    </div>
  )
}
